import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    RefreshCw,
    Eye,
    CreditCard,
    UserCog,
    Shield,
    Clock,
    User
} from "lucide-react";

interface ActivityLog {
    id: string;
    action: string;
    details: Record<string, any>;
    created_at: string;
    admin_email: string | null;
    target_email: string | null;
}

const ACTION_CONFIG: Record<string, { icon: any; label: string; color: string }> = {
    view_user: { icon: Eye, label: "Ver usuário", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    adjust_credits: { icon: CreditCard, label: "Ajustar créditos", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    masquerade: { icon: UserCog, label: "Masquerade", color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" },
    update_subscription: { icon: Shield, label: "Atualizar assinatura", color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
};

export function AdminActivityFeed() {
    const { session } = useAuth();
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        loadLogs();
    }, []);

    const loadLogs = async () => {
        setRefreshing(true);
        try {
            const { data, error } = await supabase.functions.invoke("admin-actions", {
                body: { action: "get_activity_logs", limit: 30 },
                headers: { Authorization: `Bearer ${session?.access_token}` },
            });

            if (error) throw error;
            setLogs(data?.logs || []);
        } catch (error) {
            console.error("Error loading activity logs:", error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Agora";
        if (diffMins < 60) return `${diffMins}m atrás`;
        if (diffHours < 24) return `${diffHours}h atrás`;
        if (diffDays < 7) return `${diffDays}d atrás`;

        return date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getActionConfig = (action: string) => {
        return ACTION_CONFIG[action] || {
            icon: Shield,
            label: action,
            color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
        };
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                    <CardTitle className="text-lg">Atividade Admin</CardTitle>
                    <CardDescription>Últimas ações dos administradores</CardDescription>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadLogs}
                    disabled={refreshing}
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                </Button>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="space-y-3">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <Skeleton className="w-8 h-8 rounded-full" />
                                <div className="flex-1">
                                    <Skeleton className="h-4 w-3/4 mb-1" />
                                    <Skeleton className="h-3 w-1/2" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : logs.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                        <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Nenhuma atividade registrada ainda.</p>
                        <p className="text-sm">As ações serão logadas automaticamente.</p>
                    </div>
                ) : (
                    <ScrollArea className="h-80">
                        <div className="space-y-3">
                            {logs.map((log) => {
                                const config = getActionConfig(log.action);
                                const ActionIcon = config.icon;

                                return (
                                    <div
                                        key={log.id}
                                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                                    >
                                        <div className={`p-2 rounded-full ${config.color}`}>
                                            <ActionIcon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge variant="secondary" className={config.color}>
                                                    {config.label}
                                                </Badge>
                                                {log.target_email && (
                                                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                                                        <User className="w-3 h-3" />
                                                        {log.target_email.split("@")[0]}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                <span>{log.admin_email?.split("@")[0] || "Admin"}</span>
                                                <span>·</span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatTime(log.created_at)}
                                                </span>
                                            </div>
                                            {log.details && Object.keys(log.details).length > 0 && (
                                                <p className="text-xs text-muted-foreground mt-1 truncate">
                                                    {JSON.stringify(log.details).slice(0, 80)}...
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>
                )}
            </CardContent>
        </Card>
    );
}
